<script lang="ts" setup>
import { computed } from 'vue';
import { type JobLocationContext } from '@factoryfixinc/ats-interfaces';
import {
  type WorkerLives,
  locationContextFor,
  workerLivesFor,
} from '@/core/jobs/copilot-activation/location-context.utils';

import OptionCard from './OptionCard.vue';

const props = withDefaults(
  defineProps<{
    modelValue: JobLocationContext | null;
    isDisabled?: boolean;
  }>(),
  { isDisabled: false },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: JobLocationContext | null): void;
}>();

const workerLives = computed<WorkerLives | null>(() => workerLivesFor(props.modelValue));

const requiresAnswer = (value: WorkerLives | null): boolean | string =>
  !!value || 'Choose where the worker must live';

function select(value: WorkerLives) {
  if (props.isDisabled) return;
  emit('update:modelValue', locationContextFor('field', value));
}
</script>

<template>
  <div
    data-test="worker-lives-question"
    class="mb-4 w-full"
    :class="{ 'pointer-events-none opacity-60': isDisabled }"
  >
    <p class="mb-3 font-sans text-xs font-bold leading-[18px] text-shade-900">
      Where must the worker live?*
    </p>
    <v-input
      :model-value="workerLives"
      :rules="[requiresAnswer]"
      validate-on="submit"
      hide-details="auto"
    >
      <div class="flex w-full flex-wrap items-stretch gap-4">
        <OptionCard
          test-id="worker-lives-near"
          title="Near a location"
          hint="Within a set distance of a city."
          :selected="workerLives === 'near'"
          :disabled="isDisabled"
          @select="select('near')"
        />
        <OptionCard
          test-id="worker-lives-anywhere"
          title="Anywhere in the U.S."
          hint="No home base requirement."
          :selected="workerLives === 'anywhere'"
          :disabled="isDisabled"
          @select="select('anywhere')"
        />
      </div>
    </v-input>
  </div>
</template>
